import { useCallback, useEffect, useRef, useState } from "react";

import {
  editorDraftStore,
  type EditorDraftBlob,
} from "./editorDraftStore";

// Writes the editor's draft to the device a moment after each change (#336,
// flow 11: «app uccisa a metà, zero azioni perse»), and removes it once the
// editor matches the server again. The read is useDraftResume's, before the
// editor mounts.
//
// The debounce is short on purpose: a kill inside it loses at most the last
// keystrokes. Unmounting flushes a pending write, which is what makes «Tieni
// la bozza» keep the last edit even when the exit comes right after it.
// `discard` (the «Scarta» exit) cancels the pending write first, so the
// unmount flush cannot bring the discarded draft back.

const WRITE_DEBOUNCE_MS = 300;

export type DraftPersistenceInput = {
  /** Storage key, or null: nothing is written or removed (no account scope,
   *  or writing paused). */
  storageKey: string | null;
  /** True while the edited value differs from the server's. */
  dirty: boolean;
  /** Builds the blob at write time, so it carries the latest baseline. */
  snapshot: () => EditorDraftBlob;
  /** Changes whenever the edited value does: the write trigger. */
  revision: unknown;
  /** When the resumed draft was written, until this editor writes its own. */
  initialSavedAtMs?: number | null;
};

export function useDraftPersistence({
  storageKey,
  dirty,
  snapshot,
  revision,
  initialSavedAtMs = null,
}: DraftPersistenceInput) {
  const [savedAtMs, setSavedAtMs] = useState<number | null>(initialSavedAtMs);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<(() => Promise<void>) | null>(null);
  const snapshotRef = useRef(snapshot);
  snapshotRef.current = snapshot;
  const discarded = useRef(false);

  const runPending = useCallback(async () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    const run = pending.current;
    pending.current = null;
    if (run) await run();
  }, []);

  const cancelPending = useCallback(() => {
    pending.current = null;
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => {
    // Writing paused (the resume question is open, say): a write scheduled
    // under the old key would rewrite the very blob the question is about.
    // A key that moves needs nothing here: the write below replaces it.
    if (storageKey === null) {
      cancelPending();
      return;
    }
    if (discarded.current) return;
    if (!dirty) {
      cancelPending();
      setSavedAtMs(null);
      void editorDraftStore.clear(storageKey);
      return;
    }
    pending.current = async () => {
      await editorDraftStore.write(storageKey, snapshotRef.current());
      setSavedAtMs(Date.now());
    };
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => void runPending(), WRITE_DEBOUNCE_MS);
  }, [storageKey, dirty, revision, runPending, cancelPending]);

  // A created program changes key (`new` → its id): the old blob would come
  // back as a second «new» draft, so it goes. A key that goes null (writing
  // paused) is not a move: nothing is removed for it.
  const prevKey = useRef(storageKey);
  useEffect(() => {
    if (storageKey === null) return;
    const prev = prevKey.current;
    prevKey.current = storageKey;
    if (prev !== null && prev !== storageKey) void editorDraftStore.clear(prev);
  }, [storageKey]);

  useEffect(() => () => void runPending(), [runPending]);

  /** Write now whatever is waiting (an exit that keeps the draft). */
  const flush = useCallback(() => runPending(), [runPending]);

  /** Drop the device draft and stop writing it (the «Scarta» exit). */
  const discard = useCallback(async () => {
    discarded.current = true;
    cancelPending();
    if (storageKey !== null) await editorDraftStore.clear(storageKey);
  }, [storageKey, cancelPending]);

  return { savedAtMs, flush, discard };
}
